import React, { createContext, useContext, useRef, useState } from "react";
import { usePlantOperations } from "./hooks/usePlantOperations";
import { useWbsOperations } from "./hooks/useWbsOperations";
import { useTreeSelection } from "./hooks/useTreeSelection";

const TreeViewContext = createContext(null);

/**
 * State for the Plant -> WBS -> Documents/Input Data/Estimation tree:
 * the loaded plants, the focused/visible selection, the pending navigation
 * confirmation, the node-id registry clicks resolve through, and the
 * add/modify/delete operations for projects and WBS.
 *
 * Consume it via `useTreeViewContext()` - never import this context directly.
 */
export const TreeViewProvider = ({
  data,
  activePlantId,
  onSelect,
  pickerMode = false,
  showLegend = true,
  collapsible = false,
  defaultListOpen,
  focusSelectedPlant = false,
  children,
}) => {
  // Pending Input Data/Estimation/Drawing node awaiting user confirmation
  // before navigating away from this page.
  const [confirmTarget, setConfirmTarget] = useState(null);

  // Collapsible mode only: the list stays hidden behind the "Projects"
  // heading until it's clicked - `defaultListOpen` overrides that initial
  // state for a consumer (e.g. AppSidebar) that wants the breadcrumb-style
  // "Projects" toggle button without starting collapsed.
  const [listOpen, setListOpen] = useState(defaultListOpen ?? !collapsible);
  // focusSelectedPlant mode only: id of the plant the list has narrowed
  // down to (null = showing every plant).
  const [focusedPlantId, setFocusedPlantId] = useState(null);

  // Rebuilt on every render while walking the tree, so node clicks can
  // resolve their full context (plant/wbs/doc) without re-parsing ids.
  const nodeTargetRef = useRef({});

  const plantOperations = usePlantOperations({
    createPlant: data.createPlant,
    updatePlant: data.updatePlant,
    deletePlant: data.deletePlant,
    wbsByPlant: data.wbsByPlant,
  });

  const wbsOperations = useWbsOperations({
    createWbs: data.createWbs,
    updateWbs: data.updateWbs,
    deleteWbs: data.deleteWbs,
    wbsByPlant: data.wbsByPlant,
    loadWbsForPlant: data.loadWbsForPlant,
  });

  const selection = useTreeSelection({
    treeData: data,
    onSelect,
    pickerMode,
    focusSelectedPlant,
    focusedPlantId,
    setFocusedPlantId,
    nodeTargetRef,
    confirmTarget,
    setConfirmTarget,
  });

  const value = {
    treeData: data,
    activePlantId,
    onSelect,
    pickerMode,
    showLegend,
    collapsible,
    focusSelectedPlant,
    listOpen,
    setListOpen,
    focusedPlantId,
    setFocusedPlantId,
    focusedPlant: selection.focusedPlant,
    visiblePlants: selection.visiblePlants,
    confirmTarget,
    setConfirmTarget,
    nodeTargetRef,
    handleSearchInput: selection.handleSearchInput,
    handleNodeSelect: selection.handleNodeSelect,
    handleConfirmOpen: selection.handleConfirmOpen,
    handleConfirmCancel: selection.handleConfirmCancel,
    openDocumentRegister: selection.openDocumentRegister,
    plantOperations,
    wbsOperations,
  };

  return (
    <TreeViewContext.Provider value={value}>
      {children}
    </TreeViewContext.Provider>
  );
};

export const useTreeViewContext = () => useContext(TreeViewContext);
